import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-polyglot';
import { observer } from 'mobx-react';

import { NotificationManager } from '@genially/design-system';
import type { PlayerPlayP2pClient } from '@genially/p2p-client';
import {
  PlayerPlayP2pClientEventType,
  PlayP2pClientEventType,
  persistPlaceholderAlias,
  restoreConfirmedAlias,
  restorePlaceholderAlias,
} from '@genially/p2p-client';
import type { GameState } from '@genially/p2p-lib';
import { PlayErrorCodes, PlayUserType } from '@genially/p2p-lib';
import { FEATURE_FLAGS } from '@genially/ui/build/constants/featureFlags';
import { generateAnonymousAlias } from '@genially/ui/build/utils/generateAnonymousAlias';

import { usePlaySessionInfo } from '../../../../../../bootstrap/application/components/app/components/PlaySessionInfoProvider/PlaySessionInfoProvider.context';
import { useLogRocketService } from '../../../../../../bootstrap/application/components/app/Routes/hooks/useLogRocketService';
import { RouteNames } from '../../../../../../bootstrap/domain/RouteNames';
import { Ping } from '../../../../../../shared/application/components/DebugPing/Ping';
import { useFetchInitialSlide } from '../../../../../../shared/application/hooks/useFetchInitialSlide';
import { useNavigation } from '../../../../../../shared/application/hooks/useNavigation';
import { useSleep } from '../../../../../../shared/application/hooks/useSleep';
import type { PlayerProps } from '../../../../../../shared/application/hooks/useValidateParams';
import { getLogger } from '../../../../../../shared/infrastructure/logger';
import { PlayerAliasModal } from './components/PlayerAliasModal/PlayerAliasModal';
import { PlayerPreLobby } from './components/PlayerPreLobby/PlayerPreLobby';
import { SessionLostOverlay } from './components/SessionLostOverlay/SessionLostOverlay';
import { createPlayerPlayP2pClient } from './utils/createPlayerPlayP2pClient';

export type PlayerClientContextType = {
  client: PlayerPlayP2pClient;
};

export const PlayerClientContext = createContext({});

export const MinimumDelayOfPreLobby = 2000;
export const TimeoutOfSessionJoin = 120000;

const TimeoutOfSessionLostOverlay = 120000;
const TimeoutOfShowHostDisconnectedNotification = 2500;
const TimeoutOfPlayerConnectedWithoutHost = 60 * 60 * 1000;

export const PlayerClientProvider = observer(
  ({ params, children }: { params: PlayerProps; children: React.ReactNode }) => {
    const t = useTranslate();

    const client = useRef<PlayerPlayP2pClient | undefined>(undefined);

    const { navigate, replaceUrl } = useNavigation();

    const { trackWithSamplingOnce } = useLogRocketService();

    const { setUser } = usePlaySessionInfo();

    const minimumDelayOfPreLobby = useSleep(MinimumDelayOfPreLobby);
    const timeoutOfSessionJoin = useSleep(TimeoutOfSessionJoin);

    const [showSessionLostOverlay, setShowSessionLostOverlay] = useState(false);

    const [connecting, setConnecting] = useState(false);

    const [isAliasModalOpen, setIsAliasModalOpen] = useState(false);
    const [isAliasModalDismissable, setIsAliasModalDismissable] = useState(true);

    const initialSlideFetch = useFetchInitialSlide(params.geniallyId);

    const isReady = useCallback(
      (
        clientToAssert: PlayerPlayP2pClient | undefined,
      ): clientToAssert is PlayerPlayP2pClient => {
        return (
          !connecting &&
          !!clientToAssert &&
          !!initialSlideFetch.data &&
          !minimumDelayOfPreLobby.running
        );
      },
      [connecting, initialSlideFetch.data, minimumDelayOfPreLobby.running],
    );

    useEffect(() => {
      if (connecting) return;
      if (!initialSlideFetch.data) return;
      if (client.current) return;

      const { geniallyId, sessionId, username: usernameFromParams } = params;

      // A username in the URL means it was explicitly chosen (legacy flow or the
      // username-in-use fallback screen) — treat it as already claimed. Otherwise,
      // restore an alias already claimed for this session, or fall back to a
      // previously-generated placeholder (or a freshly generated one) the player
      // has yet to claim — kept stable across reloads so it doesn't change under
      // them before they've had the chance to.
      const restoredAlias = usernameFromParams
        ? undefined
        : restoreConfirmedAlias(sessionId);

      const username =
        usernameFromParams ??
        restoredAlias ??
        restorePlaceholderAlias(sessionId) ??
        generateAnonymousAlias();

      const isAnonymous = !usernameFromParams && !restoredAlias;

      if (isAnonymous) {
        persistPlaceholderAlias(sessionId, username);
      }

      const newClient = createPlayerPlayP2pClient({
        geniallyId,
        username,
        isAnonymous,
        sessionId,
        initialSlide: initialSlideFetch.data,
      });

      const allowToJoin = () => {
        setConnecting(false);
        timeoutOfSessionJoin.stop();

        // The host's flags only reach us with this message, and useSleep captures its delay at
        // mount, so the padding has to be cancelled here rather than configured up front.
        if (newClient.hasFeatureFlag(FEATURE_FLAGS.FEAT_LT4_PLAY_OPEN_MODE)) {
          minimumDelayOfPreLobby.stop();
        }

        // The host's game state arrives just before this message, so `aliasMode` is
        // already the host's real choice here. `onJoin` is only ever evaluated at this
        // point: a host switching to it mid-session doesn't retroactively gate players
        // who already entered.
        if (newClient.game.aliasMode === 'onJoin' && newClient.isAnonymous) {
          setIsAliasModalDismissable(false);
          setIsAliasModalOpen(true);
        }

        newClient.off(PlayerPlayP2pClientEventType.AllowPlayerToJoin, allowToJoin);
      };

      newClient.on(PlayerPlayP2pClientEventType.AllowPlayerToJoin, allowToJoin);

      newClient.on(PlayerPlayP2pClientEventType.AliasRequired, () => {
        setIsAliasModalDismissable(true);
        setIsAliasModalOpen(true);
      });

      // The host switched to `never` — release anyone stuck behind the modal, including
      // the non-dismissable join gate that hides the content. The client replays the
      // withheld answer itself, so nothing is lost here.
      newClient.on(PlayerPlayP2pClientEventType.AliasNoLongerRequired, () => {
        setIsAliasModalOpen(false);
      });

      const onGameFinished = () => {
        setConnecting(false);
        timeoutOfSessionJoin.stop();

        newClient.off(PlayerPlayP2pClientEventType.GameFinished, onGameFinished);
      };

      newClient.on(PlayerPlayP2pClientEventType.GameFinished, onGameFinished);

      newClient.on(PlayerPlayP2pClientEventType.Kicked, () => {
        replaceUrl({
          to: RouteNames.PlayerKicked,
        });
      });

      newClient.on(PlayerPlayP2pClientEventType.InvalidUsername, () => {
        replaceUrl({
          to: RouteNames.PlayerUsernameSelection,
          params: {
            geniallyId,
            sessionId,
          },
        });
      });

      newClient.on(PlayerPlayP2pClientEventType.NoGameFound, () => {
        NotificationManager.error(t('__new.play.accessError.notification'));

        navigate({
          to: RouteNames.PlayerSessionFinder,
        });
      });

      newClient.on(PlayerPlayP2pClientEventType.SessionBlocked, () => {
        navigate({
          to: RouteNames.PlayerSessionBlocked,
          params: {
            username: newClient.username,
            geniallyId: newClient.geniallyId,
            sessionId: newClient.sessionId,
          },
        });
      });

      newClient.on(PlayerPlayP2pClientEventType.PlayersLimitReached, () => {
        navigate({
          to: RouteNames.PlayersLimitReached,
        });
      });

      newClient.on(PlayP2pClientEventType.Error, code => {
        if (code === PlayErrorCodes.UsernameInUse) {
          newClient.dispose();

          navigate({
            to: RouteNames.PlayerUsernameInUse,
            params: {
              username: newClient.username,
              geniallyId: newClient.geniallyId,
              sessionId: newClient.sessionId,
            },
          });
        }
      });

      const track = (game?: GameState) => {
        if (game === undefined || game.isTrackedInLogRocket) {
          trackWithSamplingOnce('play_participant_lobby', 10);
        }

        newClient.off(PlayerPlayP2pClientEventType.MarkAsTrackedInLogRocket, track);
        newClient.off(PlayerPlayP2pClientEventType.GameChanged, track);
      };

      newClient.on(PlayerPlayP2pClientEventType.MarkAsTrackedInLogRocket, track);
      newClient.on(PlayerPlayP2pClientEventType.GameChanged, track);

      setUser({
        type: PlayUserType.Player,
        geniallyId,
        sessionId,
        username,
      });

      setConnecting(true);
      client.current = newClient;

      newClient.start();
    }, [
      connecting,
      initialSlideFetch.data,
      params,
      minimumDelayOfPreLobby,
      timeoutOfSessionJoin,
      navigate,
      t,
      replaceUrl,
      setUser,
      trackWithSamplingOnce,
    ]);

    const isGameFinished = !!client.current?.game?.isFinished;
    const isConnected = !!client.current?.isConnectedToHost;

    useEffect(() => {
      if (!isConnected && !isGameFinished) {
        let hostDisconnectedNotification:
          | ReturnType<typeof NotificationManager.error>
          | undefined;

        const removeNotification = () => {
          if (hostDisconnectedNotification) {
            NotificationManager.remove(hostDisconnectedNotification);
          }
        };

        // Prevent micro-disconnections from showing the notification
        const showHostDisconnectedNotificationTimeout = window.setTimeout(() => {
          hostDisconnectedNotification = NotificationManager.error(
            t('__new.play.internetError.notification'),
            0,
          );
        }, TimeoutOfShowHostDisconnectedNotification);

        const showSessionLostOverlayTimeout = window.setTimeout(() => {
          setShowSessionLostOverlay(true);
          removeNotification();
        }, TimeoutOfSessionLostOverlay);

        const timeoutIdOfPlayerConnectedWithoutHost = window.setTimeout(() => {
          client.current?.dispose();
        }, TimeoutOfPlayerConnectedWithoutHost);

        return () => {
          clearTimeout(showHostDisconnectedNotificationTimeout);
          clearTimeout(showSessionLostOverlayTimeout);
          clearTimeout(timeoutIdOfPlayerConnectedWithoutHost);

          setShowSessionLostOverlay(false);

          removeNotification();
        };
      }
    }, [t, isConnected, isGameFinished]);

    useEffect(() => {
      return () => {
        if (client.current) {
          client.current.dispose();
        }
      };
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      if (timeoutOfSessionJoin.ended) {
        getLogger().error('Redirecting to /500', {
          geniallyId: params.geniallyId,
          sessionId: params.sessionId,
          page: 'HostClientProvider (timeoutOfSessionJoin.ended)',
          username: client.current?.username,
          running: timeoutOfSessionJoin.running,
          ended: timeoutOfSessionJoin.ended,
          stopped: timeoutOfSessionJoin.stopped,
        });

        navigate({ to: RouteNames.UnknownError });
      }
    }, [navigate, timeoutOfSessionJoin]);

    useEffect(() => {
      return () => {
        Ping.reset();
      };
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      if (client.current?.pingTime) {
        Ping.value = client.current.pingTime;
      }
    }, [client.current?.pingTime]);

    if (!isReady(client.current)) {
      return <PlayerPreLobby />;
    }

    const currentClient = client.current;

    const contextValue: PlayerClientContextType = {
      client: currentClient,
    };

    const isMandatoryAliasGate = isAliasModalOpen && !isAliasModalDismissable;

    const handleAliasSubmit = (alias: string) => currentClient.claimUsername(alias);

    const handleAliasModalClose = () => {
      setIsAliasModalOpen(false);
      currentClient.cancelPendingAnswer();
    };

    return (
      <PlayerClientContext.Provider value={contextValue}>
        <SessionLostOverlay show={showSessionLostOverlay} />
        <PlayerAliasModal
          show={isAliasModalOpen}
          dismissable={isAliasModalDismissable}
          onClose={handleAliasModalClose}
          onSubmit={handleAliasSubmit}
        />
        {!isMandatoryAliasGate && children}
      </PlayerClientContext.Provider>
    );
  },
);
